import { renderBasemapSnapshot } from "./basemap";
import { lonLatToWorldPx } from "../shared/geo";
import { HEAT_FADE_MS, heatColorAt, heatFade, heatWeightForLook } from "../shared/heat";
import type { Detection } from "../shared/types";
import { formatClock } from "./format";

/** Floor so a 10-minute ignition still has a readable scrub. */
export const REPLAY_MIN_SPAN_MS = 60 * 60 * 1000;
const LOOP_MS_MIN = 4800;
const LOOP_MS_MAX = 8000;
/** 24 KDEs across the event, then crossfade — not one canvas per hour. */
const HEAT_BUCKETS = 24;
/** Quarter-res KDE, then upscale. Soft, not jaggy sensor cells. */
const HEAT_SCALE = 4;

/** First look → last look, same span as the FRP chart. */
export function replaySpan(detections: { observed_at: string }[], nowMs = Date.now()): { t0: number; t1: number } {
  let first = Infinity;
  let last = -Infinity;
  for (const d of detections) {
    const t = Date.parse(d.observed_at);
    if (!Number.isFinite(t)) continue;
    if (t < first) first = t;
    if (t > last) last = t;
  }
  if (!Number.isFinite(first) || !Number.isFinite(last)) {
    return { t0: nowMs - REPLAY_MIN_SPAN_MS, t1: nowMs };
  }
  const span = Math.max(last - first, REPLAY_MIN_SPAN_MS);
  return { t0: last - span, t1: last };
}

/** Playhead on [t0, t1] — the event clock, not a rolling 24 h window. */
export function replayPlayhead(t0: number, t1: number, phase: number): { t0: number; t1: number; playhead: number } {
  const span = Math.max(t1 - t0, 1);
  const p = Math.min(1, Math.max(0, phase));
  return { t0, t1, playhead: t0 + p * span };
}

/** A two-day fire gets a bit more loop time than a one-hour look. */
export function replayLoopMs(spanMs: number): number {
  const days = Math.max(spanMs, REPLAY_MIN_SPAN_MS) / (24 * 3600 * 1000);
  return Math.round(Math.min(LOOP_MS_MAX, Math.max(LOOP_MS_MIN, days * 4800)));
}

type Bounds = { west: number; south: number; east: number; north: number };

type MapBg = {
  canvas: HTMLCanvasElement;
  z: number;
  bounds: Bounds;
};

/**
 * Fast scrub of this fire's recorded life — same first/last look as the FRP chart.
 */
export function startReplay(canvas: HTMLCanvasElement, detections: Detection[], nowMs = Date.now()): () => void {
  const { t0, t1 } = replaySpan(detections, nowMs);
  const loopMs = replayLoopMs(t1 - t0);
  // Pixels from before t0 still feed the trail at the start of the loop.
  const trailSince = t0 - HEAT_FADE_MS;
  const frames = detections
    .filter((d) => {
      const t = Date.parse(d.observed_at);
      return t >= trailSince && t <= t1;
    })
    .sort((a, b) => a.observed_at.localeCompare(b.observed_at));

  let stopped = false;
  let raf = 0;
  let bg: MapBg | null = null;
  const backgroundAbort = new AbortController();
  const bounds = frames.length ? paddedBounds(frames) : null;
  if (bounds) {
    void loadMapBg(bounds, canvas.clientWidth || 348, canvas.clientHeight || 168, backgroundAbort.signal).then((loaded) => {
      if (!stopped) bg = loaded;
    });
  }

  const paint = (t: number) => {
    if (stopped) return;
    drawFrame(canvas, frames, t, bg, t0, t1, loopMs);
    raf = requestAnimationFrame(paint);
  };
  raf = requestAnimationFrame(paint);

  return () => {
    stopped = true;
    backgroundAbort.abort();
    cancelAnimationFrame(raf);
    // Drop the 24 bucket canvases so the next fire does not reuse the wrong KDE.
    heatFrames.clear();
  };
}

function drawFrame(
  canvas: HTMLCanvasElement,
  detections: Detection[],
  nowPerf: number,
  bg: MapBg | null,
  t0: number,
  t1: number,
  loopMs: number,
): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = canvas.clientWidth || 348;
  const cssH = canvas.clientHeight || 168;
  if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  ctx.fillStyle = "#0c1014";
  ctx.fillRect(0, 0, cssW, cssH);
  if (bg) {
    // Lift the dark map so roads stay readable under the heat.
    ctx.filter = "brightness(1.55) contrast(1.12) saturate(1.15)";
    ctx.drawImage(bg.canvas, 0, 0, cssW, cssH);
    ctx.filter = "none";
  } else {
    drawGrid(ctx, cssW, cssH);
  }

  if (detections.length === 0) {
    ctx.fillStyle = "#8d9088";
    ctx.font = "12px ui-sans-serif, system-ui, sans-serif";
    ctx.fillText("No detections to replay", 12, cssH / 2);
    return;
  }

  const bounds = bg?.bounds ?? paddedBounds(detections);
  const z = bg?.z ?? 10;
  const phase = (nowPerf % loopMs) / loopMs;
  const { playhead } = replayPlayhead(t0, t1, phase);
  const f = phase * (HEAT_BUCKETS - 1);
  const i0 = Math.min(HEAT_BUCKETS - 1, Math.floor(f));
  const i1 = Math.min(HEAT_BUCKETS - 1, i0 + 1);
  const mix = f - i0;
  const p0 = t0 + (i0 / (HEAT_BUCKETS - 1)) * (t1 - t0);
  const p1 = t0 + (i1 / (HEAT_BUCKETS - 1)) * (t1 - t0);
  const a = heatmapFrame(detections, bounds, z, cssW, cssH, p0, i0);
  const b = heatmapFrame(detections, bounds, z, cssW, cssH, p1, i1);

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // Keep the basemap in charge — heat is a wash, not a curtain.
  ctx.globalAlpha = 0.55 * (1 - mix);
  ctx.drawImage(a, 0, 0, cssW, cssH);
  ctx.globalAlpha = 0.55 * mix;
  ctx.drawImage(b, 0, 0, cssW, cssH);
  ctx.globalAlpha = 1;

  const label = formatClock(new Date(playhead).toISOString());
  ctx.fillStyle = "rgba(12, 16, 20, 0.72)";
  ctx.fillRect(8, cssH - 26, 168, 16);
  ctx.fillStyle = "#ecece8";
  ctx.font = "11px ui-sans-serif, system-ui, sans-serif";
  ctx.fillText(label, 12, cssH - 14);
}

/** One canvas per hour-bucket. A single slot evicted the neighbor every crossfade. */
const heatFrames = new Map<string, HTMLCanvasElement>();

/**
 * Quarter-res KDE, same fade/weight as the map, then upscaled.
 */
function heatmapFrame(
  detections: Detection[],
  bounds: Bounds,
  z: number,
  cssW: number,
  cssH: number,
  playhead: number,
  bucket: number,
): HTMLCanvasElement {
  const key = `${cssW}x${cssH}:${z}:${bounds.west.toFixed(4)}:${detections[0]?.id ?? ""}:${bucket}`;
  const hit = heatFrames.get(key);
  if (hit) return hit;

  const hw = Math.max(1, Math.round(cssW / HEAT_SCALE));
  const hh = Math.max(1, Math.round(cssH / HEAT_SCALE));
  const density = new Float32Array(hw * hh);

  for (const d of detections) {
    const ts = Date.parse(d.observed_at);
    const fade = heatFade(playhead - ts);
    if (fade <= 0.02) continue;
    const [x, y] = projectMercator(d.lon, d.lat, bounds, z, hw, hh);
    // S3 has no MW — still splat a modest cell so the sidebar replay matches the map.
    const weight = heatWeightForLook(d.source, d.frp_mw);
    // Large vs the low-res grid so VIIRS/MTG cells melt together.
    splatGaussian(density, hw, hh, x, y, 5 + weight * 8, weight * fade * 0.55);
  }

  const canvas = document.createElement("canvas");
  canvas.width = hw;
  canvas.height = hh;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    heatFrames.set(key, canvas);
    return canvas;
  }
  const img = ctx.createImageData(hw, hh);
  const data = img.data;
  for (let i = 0; i < density.length; i++) {
    // MapLibre-style density: 1 − e^(−k·Σkernels)
    const t = 1 - Math.exp(-density[i]! * 1.35);
    if (t < 0.01) continue;
    const c = heatColorAt(t);
    const o = i * 4;
    data[o] = Math.round(c.r);
    data[o + 1] = Math.round(c.g);
    data[o + 2] = Math.round(c.b);
    data[o + 3] = Math.round(c.a * 255);
  }
  ctx.putImageData(img, 0, 0);
  heatFrames.set(key, canvas);
  return canvas;
}

function splatGaussian(
  density: Float32Array,
  w: number,
  h: number,
  cx: number,
  cy: number,
  radius: number,
  amplitude: number,
): void {
  const r = Math.ceil(radius);
  const x0 = Math.max(0, Math.floor(cx - r));
  const x1 = Math.min(w - 1, Math.ceil(cx + r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const y1 = Math.min(h - 1, Math.ceil(cy + r));
  const twoSig2 = 2 * (radius / 2.4) ** 2;
  const r2 = radius * radius;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d2 = dx * dx + dy * dy;
      if (d2 > r2) continue;
      density[y * w + x] += amplitude * Math.exp(-d2 / twoSig2);
    }
  }
}

async function loadMapBg(bounds: Bounds, cssW: number, cssH: number, signal: AbortSignal): Promise<MapBg | null> {
  const snapshot = await renderBasemapSnapshot({ bounds, width: cssW, height: cssH, signal });
  // Projection is normalized within the rendered bounds, so any Mercator zoom works.
  return snapshot ? { ...snapshot, z: 10 } : null;
}

function projectMercator(lon: number, lat: number, b: Bounds, z: number, w: number, h: number): [number, number] {
  const [wx, wy] = lonLatToWorldPx(lon, lat, z);
  const [westPx, northPx] = lonLatToWorldPx(b.west, b.north, z);
  const [eastPx, southPx] = lonLatToWorldPx(b.east, b.south, z);
  const x = ((wx - westPx) / Math.max(eastPx - westPx, 1)) * w;
  const y = ((wy - northPx) / Math.max(southPx - northPx, 1)) * h;
  return [x, y];
}

function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.strokeStyle = "rgba(255,255,255,0.05)";
  ctx.lineWidth = 1;
  for (let x = 24; x < w; x += 24) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  for (let y = 24; y < h; y += 24) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
}

function paddedBounds(detections: Detection[]): Bounds {
  let west = detections[0]!.lon;
  let east = detections[0]!.lon;
  let south = detections[0]!.lat;
  let north = detections[0]!.lat;
  for (const d of detections) {
    west = Math.min(west, d.lon);
    east = Math.max(east, d.lon);
    south = Math.min(south, d.lat);
    north = Math.max(north, d.lat);
  }
  const pad = Math.max(0.04, (east - west) * 0.35, (north - south) * 0.35);
  return { west: west - pad, east: east + pad, south: south - pad, north: north + pad };
}
